import { CommonModule } from '@angular/common';
import { Component, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { dayjs } from '@karve.it/core';
import type { AegisOptionValue } from '@move-right/aegis/core/documentOptions';
import { Store } from '@ngrx/store';

import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MessageModule } from 'primeng/message';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TooltipModule } from 'primeng/tooltip';

import { SharedModule } from '../shared.module';

import { AegisDocumentPreviewComponent } from './aegis-document-preview.component';
import { CreateDocumentDialogActions } from './create-document-dialog.actions';
import { EventChoiceView, OptionControlView, selectCreateDocumentDialogViewModel } from './create-document-dialog.selectors';
import { AegisDocumentPreview, CreateDocumentSubmission } from './create-document-dialog.state';

@Component({
  selector: 'app-create-document-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    ButtonModule,
    CalendarModule,
    CheckboxModule,
    DialogModule,
    DropdownModule,
    InputNumberModule,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    MessageModule,
    OverlayPanelModule,
    ProgressSpinnerModule,
    SplitButtonModule,
    TooltipModule,
    AegisDocumentPreviewComponent,
  ],
  templateUrl: './create-document-dialog.component.html',
  styleUrl: './create-document-dialog.component.scss',
})
export class CreateDocumentDialogComponent {

  public actions = CreateDocumentDialogActions;

  public viewModel$ = this.store.select(selectCreateDocumentDialogViewModel);

  public renderError: { preview: AegisDocumentPreview; message: string } | undefined;

  private submissionMenus = new Map<string, MenuItem[]>();

  private hidChatWidget = false;

  constructor(
    public store: Store,
    private host: ElementRef<HTMLElement>,
  ) {}

  public submissionMenu(documentsLabel: string): MenuItem[] {
    let menu = this.submissionMenus.get(documentsLabel);
    if (!menu) {
      menu = [
        {
          label: `Create ${documentsLabel} without sending`,
          icon: 'pi pi-file',
          command: () => this.store.dispatch(this.actions.createWithoutSendingMenuItemClicked()),
        },
      ];
      this.submissionMenus.set(documentsLabel, menu);
    }
    return menu;
  }

  public renderErrorFor(preview: AegisDocumentPreview | undefined) {
    return preview && this.renderError?.preview === preview ? this.renderError.message : undefined;
  }

  public reviewDocument(docType: string) {
    this.store.dispatch(this.actions.needsAttentionLinkClicked({ docType }));
    this.host.nativeElement
      .querySelector(`.checklist-inline [data-doc-type="${CSS.escape(docType)}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  public submit(submission: CreateDocumentSubmission) {
    this.store.dispatch(this.actions.submitButtonClicked({ submission }));
  }

  public setOption(name: string, value: AegisOptionValue | undefined) {
    this.store.dispatch(this.actions.optionChanged({ name, value }));
  }

  public toggleEvent(control: OptionControlView & { kind: 'events' }, event: EventChoiceView) {
    const selected = control.events.filter((e) => e.selected).map((e) => e.id);
    const next = event.selected ? selected.filter((id) => id !== event.id) : [ ...selected, event.id ];
    if (!next.length) return;
    const selectable = control.events.filter((e) => !e.disabled);
    const all = selectable.every((e) => next.includes(e.id));
    this.setOption(control.option.name, all ? [] : next);
  }

  public selectAllEvents(control: OptionControlView & { kind: 'events' }) {
    this.setOption(control.option.name, []);
  }

  public isLastSelectedEvent(control: OptionControlView & { kind: 'events' }, event: EventChoiceView) {
    return event.selected && control.events.filter((e) => e.selected).length === 1;
  }

  public toggleTransaction(control: OptionControlView & { kind: 'transactions' }, transactionId: string) {
    const selected = control.transactions.filter((t) => t.selected).map((t) => t.id);
    const next = selected.includes(transactionId)
      ? selected.filter((id) => id !== transactionId)
      : [ ...selected, transactionId ];
    this.setOption(control.option.name, next.length ? next : undefined);
  }

  public setMoney(name: string, major: number | null) {
    this.setOption(name, major === null || major === undefined ? undefined : Math.round(major * 100));
  }

  public setDate(name: string, date: Date | null) {
    this.setOption(name, date ? dayjs(date).format('YYYY-MM-DD') : undefined);
  }

  public dateValue(value: AegisOptionValue | undefined): Date | undefined {
    return typeof value === 'string' ? dayjs(value, 'YYYY-MM-DD').toDate() : undefined;
  }

  public moneyValue(value: AegisOptionValue | undefined): number | undefined {
    return typeof value === 'number' ? value / 100 : undefined;
  }

  public textValue(value: AegisOptionValue | undefined): string {
    return typeof value === 'string' ? value : '';
  }

  public hideChatWidgetOnMobile() {
    const tawk = (window as any).Tawk_API;
    if (!window.matchMedia('(max-width: 48rem)').matches || typeof tawk?.hideWidget !== 'function') return;
    if (tawk.isChatHidden?.()) return;
    tawk.hideWidget();
    this.hidChatWidget = true;
  }

  public restoreChatWidget() {
    if (!this.hidChatWidget) return;
    this.hidChatWidget = false;
    (window as any).Tawk_API?.showWidget?.();
  }

  public close() {
    this.store.dispatch(this.actions.dialogClosed());
  }

  public trackByName = (_index: number, control: OptionControlView) => control.option.name;

  public trackById = (_index: number, item: { id: string }) => item.id;

  public trackByDocType = (_index: number, item: { docType: string }) => item.docType;
}
